/**
 * Dashboard Helper Functions
 * Utility functions specific to dashboard
 */
const DashboardHelpers = {
  /**
   * Poll until Google Maps API (importLibrary) is available — same pattern as Tabulasi.
   * @param {number} timeoutMs
   * @returns {Promise<void>}
   */
  waitForGoogleMaps(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }

      const startedAt = Date.now();
      const timerId = setInterval(() => {
        if (window.google?.maps?.importLibrary) {
          clearInterval(timerId);
          resolve();
        } else if (Date.now() - startedAt > timeoutMs) {
          clearInterval(timerId);
          reject(new Error('Google Maps API tidak tersedia'));
        }
      }, 100);
    });
  },

  /**
   * Show graceful fallback inside #map when Google Maps fails to load.
   * @param {string} message
   */
  showMapLoadError(message) {
    const mapEl = document.getElementById('map');
    if (!mapEl) return;

    mapEl.innerHTML = `
      <div class="d-flex align-items-center justify-content-center text-muted p-4" style="min-height: 320px;">
        <div class="text-center">
          <i class="mdi mdi-map-marker-off-outline font-24 d-block mb-2"></i>
          <span>${message}</span>
        </div>
      </div>
    `;
  },

  /**
   * Stasiun muka air tanah (station type AGWLR)
   * @param {string} stationType
   * @returns {boolean}
   */
  isAgwlr(stationType) {
    return String(stationType || '').toUpperCase() === 'AGWLR';
  },

  /**
   * Label singkat ketinggian: MAT (tanah) atau TMA (permukaan)
   * @param {string} stationType
   * @returns {string}
   */
  getAwlrLevelShortLabel(stationType) {
    return this.isAgwlr(stationType) ? 'MAT' : 'TMA';
  },

  /**
   * Label panjang ketinggian
   * @param {string} stationType
   * @returns {string}
   */
  getAwlrLevelFullLabel(stationType) {
    return this.isAgwlr(stationType)
      ? 'Tinggi Muka Air Tanah'
      : 'Tinggi Muka Air';
  },

  /**
   * Label elevasi: EMAT (tanah) atau Elevasi (permukaan)
   * @param {string} stationType
   * @returns {string}
   */
  getAwlrElevationLabel(stationType) {
    return this.isAgwlr(stationType) ? 'Elv. MAT' : 'Elevasi';
  },

  /**
   * Normalize cross-section flag from API or data attribute
   * @param {boolean|string|undefined|null} value
   * @returns {boolean}
   */
  parseHasCrossSection(value) {
    return value === true || value === 'true';
  },

  /**
   * Format angka gaya Indonesia (titik ribuan, koma desimal).
   * min 0 desimal, max 3 desimal.
   * @param {number|string} num - Number to format
   * @returns {string} Formatted number
   */
  formatNumber(num) {
    if (num == null || num === '') return '';
    const number = typeof num === 'number' ? num : parseFloat(num);
    if (Number.isNaN(number)) return '';

    return number.toLocaleString('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  },

  /**
   * Satuan volume Flow Meter → selalu tampil proper sebagai m³ (meter kubik).
   * @param {object|string|null|undefined} stationOrUnit
   * @returns {string}
   */
  getFlowMeterUnit(stationOrUnit) {
    const fallback =
      (typeof DashboardConstants !== 'undefined' &&
        DashboardConstants.FLOW_METER_UNIT) ||
      'm³';
    const raw =
      typeof stationOrUnit === 'string' || stationOrUnit == null
        ? stationOrUnit
        : stationOrUnit.unitDisplay ||
          stationOrUnit.UnitDisplay ||
          stationOrUnit.unit ||
          stationOrUnit.Unit;
    const text = raw == null ? '' : String(raw).trim();
    if (!text) return fallback;

    const normalized = text
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/³/g, '3')
      .replace(/\^3/g, '3');

    if (
      normalized === 'm3' ||
      normalized === 'cum' ||
      normalized.includes('kubik') ||
      normalized.includes('cubic')
    ) {
      return fallback;
    }

    return text;
  },

  /**
   * Format nilai Flow Meter + satuan m³ (untuk card/popup).
   * @param {number|string|null|undefined} value
   * @param {object|string|null|undefined} stationOrUnit
   * @param {{ asHtml?: boolean }} [options]
   * @returns {string}
   */
  formatFlowMeterValue(value, stationOrUnit, options = {}) {
    if (value == null || value === '' || value === '-') return '-';
    const formatted = this.formatNumber(value);
    if (!formatted) return '-';
    const unit = this.getFlowMeterUnit(stationOrUnit);
    if (options.asHtml === false) return `${formatted} ${unit}`;
    return `${formatted} <sup>${unit}</sup>`;
  },

  /**
   * Check if datetime is less than specified hours from now
   * @param {string|Date} dateTime - DateTime to check
   * @param {number} hours - Hours threshold (default 24)
   * @returns {boolean}
   */
  isLessThanHours(dateTime, hours = 24) {
    if (!dateTime) return false;
    const currentDate = new Date();
    const inputDate = new Date(dateTime);
    const diffInHours = (currentDate - inputDate) / (1000 * 60 * 60);
    return diffInHours < hours;
  },

  /**
   * Check online status based on reading time
   * @param {string|Date} readingAt - Last reading datetime
   * @returns {boolean}
   */
  checkOnlineStatus(readingAt) {
    if (!readingAt) return false;

    const reading = moment.parseZone(readingAt);
    const current = moment();
    const diffInHours = current.diff(reading, 'hours', true);

    return diffInHours <= DashboardConstants.ONLINE_THRESHOLD.HOURS;
  },

  /**
   * Get intensity name by code
   * @param {number} status - Intensity status code
   * @returns {string} Intensity name
   */
  getIntensityName(status) {
    const names = {
      0: 'Tidak Ada Hujan',
      1: 'Hujan Ringan',
      2: 'Hujan Sedang',
      3: 'Hujan Lebat',
      4: 'Hujan Sangat Lebat',
    };
    const code = Number(status);
    if (Number.isNaN(code)) return '-';
    return names[code] || '-';
  },

  /**
   * Get intensity icon filename
   * @param {number} status - Intensity status code
   * @returns {string} Icon filename
   */
  getIntensityIcon(status) {
    const timeOfDay = this.getTimeOfDay();

    const icons = {
      0: `berawan-${timeOfDay}.png`,
      1: 'hujan-ringan.png',
      2: 'hujan-sedang.png',
      3: 'hujan-lebat.png',
      4: 'hujan-sangat-lebat.png',
    };

    return icons[status] || 'berawan.png';
  },

  /**
   * Get time of day (pagi, siang, sore, malam)
   * @returns {string} Time of day
   */
  getTimeOfDay() {
    const hour = moment().hours();

    if (hour >= 5 && hour < 10) return 'pagi';
    if (hour >= 10 && hour < 17) return 'siang';
    if (hour >= 17 && hour < 19) return 'sore';
    return 'malam';
  },

  /**
   * Get intensity icon HTML for map
   * @param {number} status - Intensity status code
   * @returns {string} HTML img tag
   */
  getIntensityIconMap(status) {
    const iconHtml = {
      0: '<img src="/images/arr/berawan.png" alt="Tidak Ada Hujan" class="avatar-xs">',
      1: '<img src="/images/arr/hujan-ringan.png" alt="Hujan Ringan" class="avatar-xs">',
      2: '<img src="/images/arr/hujan-sedang.png" alt="Hujan Sedang" class="avatar-xs">',
      3: '<img src="/images/arr/hujan-lebat.png" alt="Hujan Lebat" class="avatar-xs">',
      4: '<img src="/images/arr/hujan-sangat-lebat.png" alt="Hujan Sangat Lebat" class="avatar-xs">',
    };
    return iconHtml[status] || '-';
  },

  /**
   * Get warning status badge HTML
   * @param {number} status - Warning status code
   * @returns {object} Badge HTML and class
   */
  getWarningStatusBadge(status) {
    const badges = {
      0: {
        html: '<span class="badge badge-awlr-normal rounded-1 ms-1 align-self-center">NORMAL</span>',
        class: 'normal',
      },
      3: {
        html: '<span class="badge badge-awlr-siaga3 rounded-1 ms-1 align-self-center blinking-content">WASPADA</span>',
        class: 'siaga3',
      },
      2: {
        html: '<span class="badge badge-awlr-siaga2 rounded-1 ms-1 align-self-center blinking-content">SIAGA</span>',
        class: 'siaga2',
      },
      1: {
        html: '<span class="badge badge-awlr-siaga1 rounded-1 ms-1 align-self-center blinking-content">AWAS</span>',
        class: 'siaga1',
      },
    };

    return (
      badges[status] || {
        html: '<span class="badge badge-awlr-nostatus rounded-1 ms-1 align-self-center">TANPA STATUS</span>',
        class: '',
      }
    );
  },

  /**
   * Format reading time
   * @param {string|Date} readingAt - Reading datetime
   * @param {string} timeZone - Timezone string
   * @returns {string} Formatted time
   */
  formatReadingTime(readingAt, timeZone) {
    if (!readingAt) return '-';

    const tz = (timeZone && String(timeZone).trim()) || 'WIB';
    const currentDate = moment().format('YYYY-MM-DD');
    const readingDate = moment.parseZone(readingAt).format('YYYY-MM-DD');
    const clock = moment.parseZone(readingAt).format('HH:mm');

    if (readingDate === currentDate) {
      return `Hari Ini, <span class="reading-time">${clock}</span> <span class="reading-tz">${tz}</span>`;
    }

    return `${moment
      .parseZone(readingAt)
      .locale('id')
      .format('D MMMM YYYY')} <span class="reading-time">${clock}</span> <span class="reading-tz">${tz}</span>`;
  },

  /**
   * Adjust padding for footers
   */
  adjustPadding() {
    const footers = document.querySelectorAll('.footer');
    const tabPanes = document.querySelectorAll('.tab-pane');

    footers.forEach((footer, index) => {
      const tabPane = tabPanes[index];
      if (footer && tabPane) {
        const footerHeight = footer.offsetHeight;
        tabPane.style.paddingBottom = `${footerHeight}px`;
      }
    });
  },

  /**
   * Debounce function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Show shimmer loading
   * @param {number} count - Number of shimmer items
   * @returns {string} Shimmer HTML
   */
  loadShimmerLastReading(count = 5) {
    let contentShimmer = '';

    for (let i = 1; i <= count; i++) {
      contentShimmer += `
                <div class="card card-station mb-2">
                    <div class="card-body">
                        <div class="d-flex justify-content-start mb-2">
                            <box class="shine me-2" style="width: 3rem;height: 3rem;"></box>
                            <div class="col-md-8">
                                <div class="box-shine-3 shine m-0"></div>
                                <div class="box-shine-1 shine m-0"></div>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text mb-2">
                            <div class="text-center">
                                <div class="box-shine-1 shine" style="width: 3rem;"></div>
                                <br>
                                <div class="box-shine-2 shine" style="width: 5rem;"></div>
                            </div>
                            <div class="text-center">
                                <div class="box-shine-1 shine" style="width: 3rem;"></div>
                                <br>
                                <div class="box-shine-2 shine" style="width: 5rem;"></div>
                            </div>
                            <div class="text-center">
                                <div class="box-shine-1 shine" style="width: 3rem;"></div>
                                <br>
                                <div class="box-shine-2 shine" style="width: 5rem;"></div>
                            </div>
                        </div>
                        <div class="box-shine-3 shine"></div>
                    </div>
                </div>
            `;
    }

    return `<div class="row"><div class="col-12">${contentShimmer}</div></div>`;
  },

  /**
   * Title case conversion
   * @param {string} str - String to convert
   * @returns {string} Title cased string
   */
  toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      if (txt === 'DKI') return txt;
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  },

  /**
   * Get percentage for progress bar
   * @param {number} value - Current value
   * @param {number} total - Total value
   * @returns {number} Percentage
   */
  getPercentageBar(value, total) {
    if (value === 0 && total === 0) return 0;
    return (value / total) * 100;
  },

  /**
   * HTML for a consistent empty state in the right sidebar
   * @param {object} options
   * @returns {string}
   */
  renderSidebarEmpty({
    icon = 'mdi-information-outline',
    title = 'Data Belum Tersedia',
    description = '',
    variant = 'default',
    compact = false,
  } = {}) {
    const variantClass =
      variant === 'error' ? ' sidebar-panel-empty--error' : '';
    const compactClass = compact ? ' sidebar-panel-empty--compact' : '';

    return `
      <div class="sidebar-panel-empty${compactClass}${variantClass}">
        <div class="sidebar-panel-empty-inner">
          <i class="mdi ${icon} sidebar-panel-empty-icon" aria-hidden="true"></i>
          <p class="sidebar-panel-empty-title">${title}</p>
          ${
            description
              ? `<p class="sidebar-panel-empty-desc">${description}</p>`
              : ''
          }
        </div>
      </div>
    `;
  },

  /**
   * HTML for a consistent loading state in the right sidebar
   * @param {object} options
   * @returns {string}
   */
  renderSidebarLoading({ message = 'Memuat...', height = 250 } = {}) {
    return `
      <div class="sidebar-panel-loading" style="min-height:${height}px">
        <span class="d-flex align-items-center justify-content-center flex-column">
          <span class="spinner-border text-secondary mb-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </span>
          <span class="sidebar-panel-loading-text">${message}</span>
        </span>
      </div>
    `;
  },

  /**
   * Shimmer skeleton placeholder for CCTV panel
   * @returns {string}
   */
  renderCctvLoadingSkeleton() {
    return `
      <div class="cctv-panel-placeholder" aria-busy="true" aria-label="Memuat CCTV">
        <div class="cctv-skeleton-feed shine">
          <i class="mdi mdi-cctv cctv-skeleton-icon" aria-hidden="true"></i>
        </div>
        <div class="cctv-skeleton-caption shine"></div>
      </div>
    `;
  },

  /**
   * Set innerHTML on a sidebar container by id
   * @param {string} containerId
   * @param {string} html
   */
  setSidebarContent(containerId, html) {
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = html;
    }
  },

  /**
   * Show error message
   * @param {string} message - Error message
   * @param {string} containerId - Container element ID
   */
  showError(message, containerId = 'error-container') {
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = `
                <div class="alert alert-danger alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-alert-circle-outline me-2"></i>
                    ${message}
                    <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                </div>
            `;
    }
  },

  /**
   * Layar kecil dashboard (mobile)
   */
  isMobileDashboard() {
    return window.matchMedia('(max-width: 767.98px)').matches;
  },

  /**
   * Scroll halaman dashboard ke paling atas (mobile)
   */
  scrollMobileDashboardToTop() {
    const scrollTargets = [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.querySelector('.content-page'),
      document.getElementById('layout-wrapper'),
    ].filter(Boolean);

    scrollTargets.forEach((el) => {
      try {
        el.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      } catch (e) {
        el.scrollTop = 0;
      }
    });

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  },

  /**
   * Scroll ke area peta; di mobile: mentok ke atas supaya map terlihat
   */
  scrollToDashboardMap() {
    if (this.isMobileDashboard()) {
      this.scrollMobileDashboardToTop();
      return;
    }

    const target =
      document.getElementById('dashboard2-map-section') ||
      document.querySelector('.row-center-2') ||
      document.getElementById('map');
    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  },

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  formatAvwrMetric(value) {
    if (value == null || value === '') {
      return '<span class="avwr-na">-</span>';
    }
    const formatted = this.formatNumber(value);
    return formatted === ''
      ? '<span class="avwr-na">-</span>'
      : formatted;
  },

  /**
   * Shared last-reading table for AVWR left-bar card and map popup.
   * @param {{ sensors?: Array }} data
   * @returns {string}
   */
  renderAvwrLastReading(data) {
    const sensors = Array.isArray(data && data.sensors) ? data.sensors : [];
    if (!sensors.length) {
      return '<div class="avwr-last-empty">Belum ada pembacaan sensor</div>';
    }

    const hasValue = (key) =>
      sensors.some((sensor) => sensor[key] != null && sensor[key] !== '');
    const showFreq = hasValue('frequency');
    const showTemp = hasValue('temperature');
    const showEkpa = hasValue('eKpa') || hasValue('ekpa');
    const showMh20 = hasValue('mH20') || hasValue('mh20');
    const showElev = hasValue('elevation');
    const channelLabel =
      sensors.length === 1 ? '1 channel' : `${sensors.length} channel`;

    const headerCell = (label, unit) => `
      <th>
        <span class="avwr-h">${label}</span>
        ${unit ? `<span class="avwr-u">${unit}</span>` : ''}
      </th>`;

    const rows = sensors
      .map((sensor) => {
        const name = this.escapeHtml(
          sensor.point || sensor.serialNumber || '-',
        );
        const isActive =
          sensor.isActive !== false && sensor.IsActive !== false;
        const frequency = !isActive
          ? '<span class="avwr-na">-</span>'
          : this.formatAvwrMetric(sensor.frequency);
        const eKpa =
          sensor.eKpa != null ? sensor.eKpa : sensor.ekpa;
        const mH20 =
          sensor.mH20 != null ? sensor.mH20 : sensor.mh20;
        return `
          <tr>
            <td class="avwr-ch" title="${name}">${name}</td>
            ${showFreq ? `<td>${frequency}</td>` : ''}
            ${showTemp ? `<td>${this.formatAvwrMetric(sensor.temperature)}</td>` : ''}
            ${showEkpa ? `<td>${this.formatAvwrMetric(eKpa)}</td>` : ''}
            ${showMh20 ? `<td>${this.formatAvwrMetric(mH20)}</td>` : ''}
            ${showElev ? `<td>${this.formatAvwrMetric(sensor.elevation)}</td>` : ''}
          </tr>`;
      })
      .join('');

    return `
      <div class="avwr-last-reading">
        <div class="avwr-last-caption">
          <span>Pembacaan terakhir</span>
          <span class="avwr-last-count">${channelLabel}</span>
        </div>
        <div class="avwr-last-scroll">
          <table class="avwr-last-table">
            <thead>
              <tr>
                ${headerCell('Channel', '')}
                ${showFreq ? headerCell('Freq', 'Hz²/1000') : ''}
                ${showTemp ? headerCell('Suhu', '°C') : ''}
                ${showEkpa ? headerCell('E kPa', '') : ''}
                ${showMh20 ? headerCell('m H₂O', '') : ''}
                ${showElev ? headerCell('Elev', 'mdpl') : ''}
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  },

  /**
   * Clear search input and re-apply status filter
   */
  clearSearch() {
    const searchInput = document.getElementById('filter-pos');
    const clearButton = document.getElementById('clear-search');

    if (searchInput) searchInput.value = '';
    if (clearButton) clearButton.style.display = 'none';

    if (typeof Dashboard !== 'undefined' && typeof Dashboard.filterStations === 'function') {
      Dashboard.filterStations('');
    } else {
      document
        .querySelectorAll(
          '.card-station-awlr, .card-station-tmat, .card-station-arr, .card-station-awlr-arr, .card-station-aws, .card-station-fm, .card-station-wq, .card-station-avwr',
        )
        .forEach((card) => (card.style.display = ''));
    }
  },
};

// Expose clearSearch to global scope for onclick event
window.clearSearch = () => DashboardHelpers.clearSearch();

// Expose debounced filter function
window.debouncedFilterPos = DashboardHelpers.debounce(function () {
  const searchInput = document.getElementById('filter-pos');
  if (searchInput) {
    Dashboard.filterStations(searchInput.value);
  }
}, 300);
